// src/pages/CaseStudyPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { toastError, toastSuccess } from "../../../utils/toast";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Upload,
  Switch,
  DatePicker,
  Popconfirm,
  Space,
  Tag,
  Card,
  Row,
  Col,
  Typography,
  Divider,
  Tooltip,
  Select,
} from "antd";
import {
  PlusOutlined,
  UploadOutlined,
  SearchOutlined,
  FilterOutlined,
  EditOutlined,
  DeleteOutlined,
  LinkOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { withBase } from "../../../config/env";

import {
  fetchCaseStudies,
  createCaseStudy,
  updateCaseStudy,
  deleteCaseStudy,
} from "../../../api/caseStudyApi";

import { fetchBlogCategories } from "../../../api/blogCategoryApi";

const { Title, Text } = Typography;

export default function CaseStudyPage() {
  const [caseStudies, setCaseStudies] = useState([]);
  const [filteredCaseStudies, setFilteredCaseStudies] = useState([]);
  const [loading, setLoading] = useState(false);

  // categories (only "Case Studies" type)
  const [categories, setCategories] = useState([]);
  const categoryMap = useMemo(() => {
    const m = new Map();
    (categories || []).forEach((c) => m.set(Number(c.id), c.title));
    return m;
  }, [categories]);

  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [form] = Form.useForm();
  const [filterForm] = Form.useForm();
  const [isSlugEditing, setIsSlugEditing] = useState(false);
  const [editorData, setEditorData] = useState("");

  // ---- Load case studies ----
  const loadCaseStudies = async () => {
    setLoading(true);
    try {
      const data = await fetchCaseStudies(); // your API already fetches case studies
      setCaseStudies(data);
      setFilteredCaseStudies(data);
    } catch (err) {
      toastError("Failed to fetch case studies");
    } finally {
      setLoading(false);
    }
  };

  // ---- Load ONLY categories of type "Case Studies" ----
  const loadCategories = async () => {
    try {
      const data = await fetchBlogCategories();
      const list = Array.isArray(data) ? data : data?.data || [];
      const onlyCaseCats = list.filter(
        (c) => (c.type || "").toLowerCase() === "case studies".toLowerCase()
      );
      setCategories(onlyCaseCats);
    } catch (err) {
      toastError("Failed to load categories");
      setCategories([]);
    }
  };

  useEffect(() => {
    loadCaseStudies();
    loadCategories();
  }, []);

  const handleCreate = () => {
    setEditData(null);
    form.resetFields();
    setEditorData("");
    setIsSlugEditing(false);
    setOpen(true);
  };

  const handleEdit = (record) => {
    setEditData(record);
    setEditorData(record.content || "");
    form.setFieldsValue({
      title: record.title,
      slug: record.slug,
      content: record.content,
      category_id: record.category_id ?? null, // prefill category
      is_published: record.is_published === "1" || record.is_published === 1,
      published_at: record.published_at ? dayjs(record.published_at) : null,
      thumbnail: record.thumbnail
        ? {
            fileList: [
              {
                uid: "-1",
                name: "thumbnail",
                status: "done",
                url: withBase(record.thumbnail),
              },
            ],
          }
        : undefined,
    });
    setIsSlugEditing(false);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteCaseStudy(id);
      toastSuccess("Case study deleted successfully");
      loadCaseStudies();
    } catch {
      toastError("Delete failed");
    }
  };

  const generateSlug = (title) =>
    title
      .toLowerCase()
      .replace(/[^\w\s]/gi, "")
      .replace(/\s+/g, "-")
      .substring(0, 60);

  const handleTitleChange = (e) => {
    const title = e.target.value;
    if (!isSlugEditing && !editData) {
      form.setFieldsValue({ slug: generateSlug(title) });
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        // keep your original API usage; ensure backend respects category_id
        type: "case", // force case study type
        title: values.title,
        slug: values.slug,
        content: editorData,
        category_id: values.category_id ?? null, // include selected category
        is_published: values.is_published ? 1 : 0,
        published_at: values.published_at
          ? values.published_at.format("YYYY-MM-DD HH:mm:ss")
          : "",
      };

      // thumbnail: support new upload and keep existing
      if (values.thumbnail?.fileList?.length) {
        const fileObj = values.thumbnail.fileList[0];
        payload.thumbnail = fileObj.originFileObj || fileObj.url;
      }

      if (editData) {
        await updateCaseStudy(editData.id, payload);
        toastSuccess("Case study updated successfully");
      } else {
        await createCaseStudy(payload);
        toastSuccess("Case study created successfully");
      }

      setOpen(false);
      form.resetFields();
      setEditorData("");
      loadCaseStudies();
    } catch (err) {
      if (!err?.errorFields) toastError("Save failed");
    }
  };

  const applyFilters = (values) => {
    let filtered = [...caseStudies];

    if (values.title) {
      filtered = filtered.filter((item) =>
        (item.title || "").toLowerCase().includes(values.title.toLowerCase())
      );
    }

    if (values.status) {
      filtered = filtered.filter((item) =>
        values.status === "published"
          ? item.is_published === "1" || item.is_published === 1
          : item.is_published === "0" || item.is_published === 0
      );
    }

    if (values.category_id) {
      filtered = filtered.filter(
        (item) => Number(item.category_id) === Number(values.category_id)
      );
    }

    if (values.dateRange && values.dateRange.length === 2) {
      const [startDate, endDate] = values.dateRange;
      filtered = filtered.filter((item) => {
        if (!item.published_at) return false;
        const publishedDate = dayjs(item.published_at);
        return (
          publishedDate.isAfter(startDate.startOf("day")) &&
          publishedDate.isBefore(endDate.endOf("day"))
        );
      });
    }

    setFilteredCaseStudies(filtered);
  };

  const resetFilters = () => {
    filterForm.resetFields();
    setFilteredCaseStudies(caseStudies);
  };

  const getCategoryName = (category_id, category_name) => {
    if (category_name) return category_name; // if API joined
    if (category_id == null) return "-";
    return categoryMap.get(Number(category_id)) || "-";
    // map fallback from cached categories
  };

  const columns = [
    {
      title: "Thumbnail",
      dataIndex: "thumbnail",
      width: 110,
      render: (val) =>
        val ? (
          <img
            src={withBase(val)}
            alt="thumb"
            style={{
              width: 90,
              height: 54,
              objectFit: "cover",
              borderRadius: 4,
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            }}
          />
        ) : (
          <div
            style={{
              width: 90,
              height: 54,
              backgroundColor: "#f0f0f0",
              borderRadius: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text type="secondary">No Image</Text>
          </div>
        ),
    },
    {
      title: "Title",
      dataIndex: "title",
      render: (text) => <Text strong>{text}</Text>,
      sorter: (a, b) => (a.title || "").localeCompare(b.title || ""),
    },
    {
      title: "Slug",
      dataIndex: "slug",
      render: (text) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <LinkOutlined style={{ marginRight: 5, color: "#888" }} />
          <Text type="secondary">{text}</Text>
        </div>
      ),
    },
    {
      title: "Category",
      dataIndex: "category_id",
      width: 200,
      render: (_val, record) => {
        const name = getCategoryName(record.category_id, record.category_name);
        return name !== "-" ? (
          <Tag
            color="blue"
            style={{ borderRadius: 12, padding: "0 10px", fontWeight: 500 }}
          >
            {name}
          </Tag>
        ) : (
          <Text type="secondary">-</Text>
        );
      },
      filters: (categories || []).map((c) => ({ text: c.title, value: c.id })),
      onFilter: (value, record) =>
        Number(record.category_id) === Number(value),
    },
    {
      title: "Status",
      dataIndex: "is_published",
      width: 120,
      render: (val) => (
        <Tag
          color={val === "1" || val === 1 ? "green" : "orange"}
          style={{ borderRadius: 12, padding: "0 10px", fontWeight: 500 }}
        >
          {val === "1" || val === 1 ? "Published" : "Draft"}
        </Tag>
      ),
      filters: [
        { text: "Published", value: "published" },
        { text: "Draft", value: "draft" },
      ],
      onFilter: (value, record) =>
        value === "published"
          ? record.is_published === "1" || record.is_published === 1
          : record.is_published === "0" || record.is_published === 0,
    },
    {
      title: "Published At",
      dataIndex: "published_at",
      width: 160,
      render: (date) =>
        date ? (
          <Text type="secondary">{dayjs(date).format("DD MMM YYYY")}</Text>
        ) : (
          <Text type="secondary">-</Text>
        ),
      sorter: (a, b) =>
        new Date(a.published_at || 0) - new Date(b.published_at || 0),
    },
    {
      title: "Actions",
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              style={{ borderRadius: 4, backgroundColor: "#f0f0f0" }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Delete this case study?"
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
              placement="left"
            >
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                style={{ borderRadius: 4 }}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Case Studies
          </Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
            style={{
              borderRadius: 6,
              height: 40,
              padding: "0 20px",
              fontWeight: 500,
            }}
          >
            New Case Study
          </Button>
        </div>
      }
      bordered={false}
      style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
    >
      {/* Filter Section */}
      <Card
        style={{
          marginBottom: 20,
          borderRadius: 12,
          border: "1px solid #f0f0f0",
        }}
        bodyStyle={{ padding: "16px 24px" }}
      >
        <Form
          form={filterForm}
          layout="vertical"
          onFinish={applyFilters}
          autoComplete="off"
        >
          <Row gutter={16}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                name="title"
                label={<Text strong>Search by Title</Text>}
              >
                <Input
                  placeholder="Enter title"
                  prefix={<SearchOutlined style={{ color: "#888" }} />}
                  allowClear
                  style={{ borderRadius: 6 }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="status" label={<Text strong>Status</Text>}>
                <Select allowClear placeholder="All statuses" style={{ borderRadius: 6 }}>
                  <Select.Option value="published">Published</Select.Option>
                  <Select.Option value="draft">Draft</Select.Option>
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item name="category_id" label={<Text strong>Category</Text>}>
                <Select
                  allowClear
                  placeholder="All categories"
                  style={{ borderRadius: 6 }}
                  options={(categories || []).map((c) => ({
                    label: c.title,
                    value: c.id,
                  }))}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={12} lg={6}>
              <Form.Item
                name="dateRange"
                label={<Text strong>Published Between</Text>}
              >
                <DatePicker.RangePicker
                  style={{ width: "100%", borderRadius: 6 }}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <div style={{ display: "flex", gap: 10 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<FilterOutlined />}
                  style={{ borderRadius: 6 }}
                >
                  Apply Filters
                </Button>
                <Button
                  onClick={() => {
                    filterForm.resetFields();
                    setFilteredCaseStudies(caseStudies);
                  }}
                  icon={<ReloadOutlined />}
                  style={{ borderRadius: 6 }}
                >
                  Reset
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </Card>

      <Table
        rowKey="id"
        dataSource={filteredCaseStudies}
        columns={columns}
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} case studies`,
          style: { padding: "0 24px" },
        }}
        style={{ borderRadius: 12, overflow: "hidden" }}
      />

      <Modal
        open={open}
        title={
          <span
            style={{
              display: "flex",
              alignItems: "center",
              fontWeight: 500,
              fontSize: 18,
            }}
          >
            {editData ? (
              <>
                <EditOutlined style={{ marginRight: 10, color: "#1890ff" }} />
                Edit Case Study
              </>
            ) : (
              <>
                <PlusOutlined style={{ marginRight: 10, color: "#1890ff" }} />
                Create Case Study
              </>
            )}
          </span>
        }
        onCancel={() => {
          setOpen(false);
          setEditorData("");
        }}
        onOk={handleSave}
        width={800}
        okText={editData ? "Update" : "Create"}
        cancelText="Cancel"
        okButtonProps={{
          style: {
            borderRadius: 6,
            height: 40,
            padding: "0 20px",
            fontWeight: 500,
          },
        }}
        cancelButtonProps={{
          style: { borderRadius: 6, height: 40, padding: "0 20px" },
        }}
        style={{ borderRadius: 12 }}
        destroyOnClose
      >
        <Divider style={{ margin: "16px 0", borderColor: "#f0f0f0" }} />
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={<Text strong>Title</Text>}
                name="title"
                rules={[{ required: true, message: "Please enter title" }]}
              >
                <Input
                  placeholder="Case study title"
                  onChange={handleTitleChange}
                  style={{ borderRadius: 6 }}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={<Text strong>Slug</Text>}
                name="slug"
                rules={[{ required: true, message: "Please enter slug" }]}
              >
                <Input
                  placeholder="case-study-slug"
                  disabled={!isSlugEditing && !editData}
                  style={{ borderRadius: 6 }}
                  suffix={
                    <Tooltip title={isSlugEditing ? "Lock Slug" : "Edit Slug"}>
                      <EditOutlined
                        onClick={() => setIsSlugEditing((s) => !s)}
                        style={{ color: "#1890ff", cursor: "pointer" }}
                      />
                    </Tooltip>
                  }
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Category dropdown (required) */}
          <Form.Item
            label={<Text strong>Category</Text>}
            name="category_id"
            rules={[{ required: true, message: "Please select a category" }]}
          >
            <Select
              placeholder="Select category"
              options={(categories || []).map((c) => ({
                label: `${c.title}`,
                value: c.id,
              }))}
              showSearch
              optionFilterProp="label"
              style={{ borderRadius: 6 }}
            />
          </Form.Item>

          <Form.Item label={<Text strong>Content</Text>} name="content">
            <div
              style={{
                border: "1px solid #d9d9d9",
                borderRadius: 6,
                overflow: "hidden",
              }}
            >
              <CKEditor
                editor={ClassicEditor}
                data={editorData}
                onChange={(event, editor) => {
                  setEditorData(editor.getData());
                }}
                config={{
                  toolbar: [
                    "heading",
                    "|",
                    "bold",
                    "italic",
                    "link",
                    "bulletedList",
                    "numberedList",
                    "blockQuote",
                    "insertTable",
                    "undo",
                    "redo",
                  ],
                }}
              />
            </div>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label={<Text strong>Thumbnail</Text>} name="thumbnail">
                <Upload
                  beforeUpload={() => false}
                  maxCount={1}
                  listType="picture-card"
                  accept="image/*"
                  style={{ width: "100%" }}
                >
                  <div>
                    <UploadOutlined style={{ fontSize: 20 }} />
                    <div style={{ marginTop: 8 }}>Upload</div>
                  </div>
                </Upload>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={<Text strong>Published</Text>}
                name="is_published"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Yes"
                  unCheckedChildren="No"
                  style={{ marginLeft: 8, backgroundColor: "#f0f0f0" }}
                />
              </Form.Item>
              <Form.Item
                label={<Text strong>Publish Date</Text>}
                name="published_at"
              >
                <DatePicker
                  showTime
                  style={{ width: "100%", borderRadius: 6 }}
                  placeholder="Select publish date"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Card>
  );
}
